<template>
  <NuxtLink v-if="!!to" :to="to" type="button" :class="classes">
    <slot />
  </NuxtLink>
  <a
    v-else-if="href"
    :class="classes"
    :disabled="disabled"
    :href="href"
    target="_blank"
    rel="nofollow noopener"
    @click="$emit('click')"
  >
    <slot />
    <SpinnerSm v-if="loading" size="1rem" class="ml-3" />
  </a>
  <button
    v-else
    :class="classes"
    :type="type"
    :disabled="disabled"
    @click="$emit('click')"
  >
    <slot />
    <SpinnerSm v-if="loading" size="1rem" class="ml-3" />
  </button>
</template>

<script setup lang="ts">
import { NuxtLink } from '#components'
import { computed } from '#imports'
import SpinnerSm from '@/components/UI/SpinnerSm.vue'

interface Props {
  to?: string
  href?: string
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'icon'
  color?: 'default' | 'gray'
  type?: 'button' | 'submit' | 'reset'
  disabled?: boolean
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  to: '',
  href: '',
  size: 'md',
  color: 'default',
  type: 'button',
  disabled: false,
  loading: false,
})

defineEmits<{
  click: []
}>()

const sizeClass = computed(() => {
  switch (props.size) {
    case 'xs': return 'px-2.5 py-1.5 text-xs rounded'
    case 'sm': return 'px-3 py-2 text-sm rounded-md'
    case 'md': return 'px-5 py-2.5 text-base rounded-md'
    case 'lg': return 'px-6 py-3 text-base rounded-md'
    case 'xl': return 'px-8 py-4 text-lg rounded-md'
    case 'icon': return 'p-2.5 text-base rounded-md'
    default: return ''
  }
})

const colorClass = computed(() => {
  switch (props.color) {
    case 'gray': return 'bg-gradient-to-r from-gray-100 to-gray-200 dark:from-ternary-dark dark:to-secondary-dark text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-ternary-dark hover:from-gray-200 hover:to-gray-300 dark:hover:from-secondary-dark dark:hover:to-ternary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 ring-gray-300 dark:ring-ternary-dark ring-offset-white dark:ring-offset-primary-dark transition-all duration-300'
    default: return 'bg-gradient-to-r from-accent to-gradient-end hover:from-gradient-end hover:to-accent text-white border border-accent/20 hover:shadow-glow focus:outline-none focus:ring-2 focus:ring-offset-2 ring-accent ring-offset-white dark:ring-offset-primary-dark transition-all duration-300'
  }
})

const classes = computed(() => {
  const common = `inline-flex items-center justify-center font-medium shadow-soft hover:shadow-soft-lg transform hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300${props.disabled ? ' opacity-50 cursor-not-allowed hover:transform-none hover:shadow-soft' : ''}`
  return `${common} ${sizeClass.value} ${colorClass.value}`
})
</script>
